import { IonAvatar, IonIcon, IonImg, useIonToast } from "@ionic/react";
import { motion } from 'framer-motion';
import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { AppContext } from "../../../AppStateProvider";
import { default as biskLogo, default as biskSvg } from "../../../assets/icons/bisk/BISK_ICON_NO_DEGREE.svg";
import useAuthService from "../../../hooks/useAuthService";
import { EnrichedBiskTransaction } from "../../../hooks/useBiskTransactions";
import { BiskTransactionType } from "../../../models/biskTransactionType";
import { formatAmount } from "../../../services/utils";
import { usePhoneVerification } from "../../../PhoneVerificationProvider";
import UsernameDisplay from "../../ComponentsUI/Username/UsernameDisplay";


interface ContainerProps {
    transaction: EnrichedBiskTransaction;
    isModal?: boolean;
    onClickObjectHandler?: (targetId: string, objectId: string, transactionType: string) => void;
    onClickUsernameHandler?: (username: string) => void;
    blurred: boolean;
    isAuthed?: boolean;
}

const BiskActivityCard: React.FC<ContainerProps> = React.memo(({
    transaction,
    isModal,
    onClickObjectHandler,
    onClickUsernameHandler,
    blurred,
    isAuthed
}) => {
    const history = useHistory();
    const { state } = useContext(AppContext);
    const { login } = useAuthService();
    const [present] = useIonToast();
    const { requirePhone } = usePhoneVerification();

    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const [isNoteVisible, setIsNoteVisible] = useState(false);

    // Use pre-computed display values from the enriched transaction
    const {
        display_origin_username: originUsername,
        display_origin_profile_pic: originProfilePic,
        display_recipient_username: recipientUsername,
        display_object_name: recipientObject,
        display_transaction_type: transactionType,
        display_extra_words: extraWords,
        display_has_note: hasNote,
        amount: transactionAmount,
    } = transaction;

    const blurredClass = useMemo(() => blurred ? 'blur-lg' : '', [blurred]);

    const presentToastLoginHandler = async (message: string) => {
        await present({
            message: message,
            duration: 3000,
            position: 'top',
            icon: biskSvg,
            buttons: [
                {
                    text: 'Login',
                    handler: async () => {
                        await login();
                    }
                },
                {
                    text: 'Cancel',
                    role: 'cancel'
                }
            ]
        });
    };

    const onClickUsername = async (username: string) => {
        if (!isAuthed) {
            await presentToastLoginHandler('Please login to view profiles');
            return;
        }

        requirePhone(() => {
            if (isModal && onClickUsernameHandler) {
                onClickUsernameHandler(username);
                return;
            }

            history.push('/profile/' + username);
        });
    }

    const onClickObject = async (targetId: string, objectId: string, transactionTypeVal: string) => {
        if (!isAuthed) {
            await presentToastLoginHandler('Please login to view the bisk action');
            return;
        }

        requirePhone(() => {
            if (isModal && onClickObjectHandler) {
                onClickObjectHandler(targetId, objectId, transactionTypeVal);
                return;
            }

            if (transactionTypeVal === BiskTransactionType.REDEEM || transactionTypeVal === BiskTransactionType.REDEEM_FOR_OBJECT) {
                history.push('/shop');
            }

            if (transactionTypeVal === BiskTransactionType.SEND_TO_OBJECT) {
                history.push('/clip/' + targetId + "/" + objectId);
            }
        });
    }

    const onFlip = () => {
        setIsFlipped(!isFlipped);
    };

    const toggleNote = () => {
        setIsNoteVisible(!isNoteVisible);
    };

    // No loading state needed - data is pre-fetched
    if (!originUsername || !transactionType) {
        return null; // Gracefully handle edge case of missing data
    }

    return (
        <div
            className={"flex flex-col border-b border-gray-900 " + blurredClass}
            style={{ minHeight: '72px' }} // Fixed min-height prevents layout shifts
        >
            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center space-x-3 cursor-pointer">
                    <IonAvatar slot="start" className="mx-0.5"
                        onClick={() => onClickUsername(originUsername)}>
                        <IonImg className="shrink w-12 h-12 flex-none rounded-full" src={originProfilePic}></IonImg>
                    </IonAvatar>
                    <div>
                        <UsernameDisplay className="font-semibold" username={originUsername}
                            userId={transaction.origin_id} onUsernameClickHandler={onClickUsername}
                            loggedInId={state.user.id} />
                        <div className="text-gray-300 text-sm">
                            {transactionType}
                            <span
                                className={`font-semibold text-indigo-300 ${recipientUsername ? 'cursor-pointer hover:underline' : ''}`}
                                onClick={() => recipientUsername && onClickUsername(recipientUsername)}>
                                {recipientUsername && ` ${recipientUsername}`}
                            </span>
                            {" bisk "}
                            <span
                                className={`font-semibold text-indigo-300 ${recipientObject ? 'cursor-pointer hover:underline' : ''}`}
                                onClick={() => recipientObject && onClickObject(transaction.target_id, transaction.object_id, transaction.transaction_type)}>
                                {extraWords} {recipientObject}
                            </span>
                        </div>
                    </div>
                </div>
                <motion.div
                    className="flex flex-none items-center justify-center w-10 h-10 bg-highlight rounded-full cursor-pointer"
                    animate={{ rotateY: isFlipped ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={onFlip}
                >
                    {isFlipped ? (
                        <motion.div
                            style={{ rotateY: "180deg" }}
                        >
                            <IonIcon src={biskLogo} size="large" style={{ rotateY: '180deg' }}
                                className="w-8 h-8 pt-2" />
                        </motion.div>
                    ) : (
                        <motion.span
                            className="text-dark text-xl font-semibold"
                        >
                            {formatAmount(transactionAmount)}
                        </motion.span>
                    )}
                </motion.div>

            </div>
            {
                hasNote &&
                transaction.transaction_type !== BiskTransactionType.WITHDRAW &&
                transaction.transaction_type !== BiskTransactionType.REDEEM_FOR_OBJECT &&
                transaction.transaction_type !== BiskTransactionType.REDEEM && (
                    <motion.div
                        initial={false}
                        animate={{ height: true ? 'auto' : 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden px-4"
                        onClick={toggleNote}
                    >
                        <p className="text-gray-200 text-sm py-1">
                            {transaction.notes}
                        </p>
                    </motion.div>
                )
            }
        </div>
    );
});

export default BiskActivityCard;